(() => {
  const instances = new Map();

  class UFCommunitySocial {
    constructor(section) {
      this.section = section;
      this.track = section.querySelector("[data-uf-social-track]");
      this.cards = Array.from(
        section.querySelectorAll("[data-uf-social-card]")
      );
      this.previousButton = section.querySelector(
        "[data-uf-social-previous]"
      );
      this.nextButton = section.querySelector("[data-uf-social-next]");

      this.autoplayEnabled =
        section.dataset.autoplay === "true" &&
        !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      this.autoplayDelay =
        Number.parseInt(section.dataset.autoplayDelay, 10) || 6000;

      this.autoplayTimer = null;
      this.scrollFrame = null;
      this.resizeTimer = null;

      if (!this.track || !this.cards.length) return;

      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleVisibilityChange =
        this.handleVisibilityChange.bind(this);

      this.bindEvents();
      this.updateNavigation();
      this.startAutoplay();
    }

    bindEvents() {
      this.previousButton?.addEventListener("click", () => {
        this.goPrevious();
        this.restartAutoplay();
      });

      this.nextButton?.addEventListener("click", () => {
        this.goNext();
        this.restartAutoplay();
      });

      this.track.addEventListener("scroll", this.handleScroll, {
        passive: true
      });

      this.track.addEventListener("pointerdown", () => {
        this.stopAutoplay();
      });

      this.track.addEventListener("pointerup", () => {
        this.startAutoplay();
      });

      this.section.addEventListener("mouseenter", () => {
        this.stopAutoplay();
      });

      this.section.addEventListener("mouseleave", () => {
        this.startAutoplay();
      });

      this.section.addEventListener("focusin", () => {
        this.stopAutoplay();
      });

      this.section.addEventListener("focusout", () => {
        this.startAutoplay();
      });

      window.addEventListener("resize", this.handleResize);
      document.addEventListener(
        "visibilitychange",
        this.handleVisibilityChange
      );
    }

    getCardStep() {
      const firstCard = this.cards[0];

      if (!firstCard) return this.track.clientWidth;

      const trackStyles = window.getComputedStyle(this.track);
      const gap = Number.parseFloat(trackStyles.columnGap) || 0;

      return firstCard.getBoundingClientRect().width + gap;
    }

    hasOverflow() {
      return this.track.scrollWidth > this.track.clientWidth + 4;
    }

    isAtStart() {
      return this.track.scrollLeft <= 4;
    }

    isAtEnd() {
      return (
        this.track.scrollLeft + this.track.clientWidth >=
        this.track.scrollWidth - 4
      );
    }

    goPrevious() {
      if (!this.hasOverflow()) return;

      if (this.isAtStart()) {
        this.track.scrollTo({
          left: this.track.scrollWidth,
          behavior: "smooth"
        });

        return;
      }

      this.track.scrollBy({
        left: -this.getCardStep(),
        behavior: "smooth"
      });
    }

    goNext() {
      if (!this.hasOverflow()) return;

      if (this.isAtEnd()) {
        this.track.scrollTo({
          left: 0,
          behavior: "smooth"
        });

        return;
      }

      this.track.scrollBy({
        left: this.getCardStep(),
        behavior: "smooth"
      });
    }

    scrollToBlock(blockId) {
      const selectedCard = this.cards.find(
        (card) => card.dataset.blockId === blockId
      );

      if (!selectedCard) return;

      this.stopAutoplay();

      this.track.scrollTo({
        left: selectedCard.offsetLeft,
        behavior: "smooth"
      });
    }

    updateNavigation() {
      const hasOverflow = this.hasOverflow();

      [this.previousButton, this.nextButton].forEach((button) => {
        if (!button) return;

        button.classList.toggle("is-hidden", !hasOverflow);
      });

      if (!hasOverflow) {
        this.stopAutoplay();
      }
    }

    handleScroll() {
      if (this.scrollFrame) {
        window.cancelAnimationFrame(this.scrollFrame);
      }

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.updateNavigation();
      });
    }

    handleResize() {
      window.clearTimeout(this.resizeTimer);

      this.resizeTimer = window.setTimeout(() => {
        this.updateNavigation();
      }, 120);
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    }

    startAutoplay() {
      if (
        !this.autoplayEnabled ||
        !this.hasOverflow() ||
        this.autoplayTimer
      ) {
        return;
      }

      this.autoplayTimer = window.setInterval(() => {
        this.goNext();
      }, this.autoplayDelay);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;

      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    destroy() {
      this.stopAutoplay();

      this.track?.removeEventListener("scroll", this.handleScroll);
      window.removeEventListener("resize", this.handleResize);
      document.removeEventListener(
        "visibilitychange",
        this.handleVisibilityChange
      );

      if (this.scrollFrame) {
        window.cancelAnimationFrame(this.scrollFrame);
      }

      window.clearTimeout(this.resizeTimer);
    }
  }

  function getRoots(scope = document) {
    const roots = [];

    if (scope.matches?.("[data-uf-community-social]")) {
      roots.push(scope);
    }

    if (scope.querySelectorAll) {
      roots.push(
        ...scope.querySelectorAll("[data-uf-community-social]")
      );
    }

    return roots;
  }

  function initialize(scope = document) {
    getRoots(scope).forEach((section) => {
      if (instances.has(section)) return;

      const instance = new UFCommunitySocial(section);
      instances.set(section, instance);
    });
  }

  function destroy(scope) {
    getRoots(scope).forEach((section) => {
      const instance = instances.get(section);

      if (!instance) return;

      instance.destroy();
      instances.delete(section);
    });
  }

  document.addEventListener("DOMContentLoaded", () => {
    initialize();
  });

  document.addEventListener("shopify:section:load", (event) => {
    initialize(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    destroy(event.target);
  });

  document.addEventListener("shopify:block:select", (event) => {
    const section = event.target.closest?.(
      "[data-uf-community-social]"
    );

    if (!section) return;

    const instance = instances.get(section);

    instance?.scrollToBlock(event.detail.blockId);
  });
})();